import { useState } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type { GetPendingInvitationsResponse } from "~/api/patients";
import { PaginatedTable } from "~/components/Shared/PaginatedTable";
import { DropdownMenu } from "~/ui";
import { formatBackendDate } from "~/utils";
import { DeleteInvitationConfirmationModal } from "../../DeleteInvitationConfirmationModal";

interface PendingTableProps {
  patients: GetPendingInvitationsResponse[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (val: number) => void;
  totalItems?: number;
  pageSize: number;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
}

export const PendingTable = ({
  patients,
  isLoading,
  currentPage,
  setCurrentPage,
  totalItems,
  pageSize,
  sorting,
  setSorting,
}: PendingTableProps) => {
  const { t } = useTranslation();
  const [selectedEmail, setSelectedEmail] = useState<string>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleRemoveInvitation = (email: string) => {
    setSelectedEmail(email);
    setDeleteModalOpen(true);
  };

  const columns: ColumnDef<GetPendingInvitationsResponse>[] = [
    {
      header: t("patients.pending_table.email"),
      accessorKey: "email",
    },
    {
      header: t("patients.pending_table.invitation_sent_by"),
      accessorKey: "invited_by",
      cell: ({ row }) => {
        const invitedBy = row.original.invited_by;
        return (
          <span>
            {invitedBy.name} {invitedBy.surname}
          </span>
        );
      },
    },
    {
      header: t("patients.pending_table.invite_expiration"),
      accessorKey: "expires_at",
      cell: ({ row }) => {
        return <span>{formatBackendDate(row.original.expires_at)}</span>;
      },
    },
    {
      id: "actions",
      cell: ({ row: { original } }) => (
        <div className="flex justify-end">
          <DropdownMenu
            options={[
              {
                label: t("patients.delete_invitation"),
                onClick: () => handleRemoveInvitation(original.email),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <PaginatedTable
        data={patients}
        columns={columns}
        isLoadingData={isLoading}
        pageSize={pageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalRows={totalItems}
        sorting={sorting}
        setSorting={setSorting}
      />
      {selectedEmail && (
        <DeleteInvitationConfirmationModal
          isOpen={deleteModalOpen}
          email={selectedEmail}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </>
  );
};
